import { useState, useEffect } from 'react';
import CookieConsent from '../../components/feature/CookieConsent';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(127);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    source: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Update meta description when waitlist count changes
  useEffect(() => {
    if (waitlistCount) {
      const event = new CustomEvent('waitlistCountUpdated', {
        detail: { count: waitlistCount }
      });
      window.dispatchEvent(event);
    }
  }, [waitlistCount]);

  // Fetch waitlist count
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/get-waitlist-count`);
        if (response.ok) {
          const data = await response.json();
          setWaitlistCount(data.total_joins);
        }
      } catch (error) {
        console.error('Failed to fetch waitlist count:', error);
      }
    };

    fetchWaitlistCount();
  }, []);

  // Scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/submit-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          email: formData.email,
          source: formData.source
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', email: '', source: '' });
        
        // Refresh waitlist count after successful submission
        const countResponse = await fetch(`${supabaseUrl}/functions/v1/get-waitlist-count`);
        if (countResponse.ok) {
          const data = await countResponse.json();
          setWaitlistCount(data.total_joins);
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Caption King',
    description: 'AI-powered Instagram caption generator that creates 5 different caption styles from your photos in seconds',
    url: 'https://tryaikit.com',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127',
    },
  };

  return (
    <>
      <SeoJsonLd data={seoData} />
      <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-white">
        {/* Cookie Consent Banner */}
        <CookieConsent />

        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/80 backdrop-blur-lg shadow-lg' : 'bg-transparent'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Caption King</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-8">
              <a href="#pricing" className="text-sm sm:text-base text-purple-600 font-medium hover:text-purple-700 transition-colors cursor-pointer">Pricing</a>
              <button onClick={scrollToWaitlist} className="px-3 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-base rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap">
                Join Waitlist →
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 sm:pt-24 pb-12 sm:pb-20 overflow-hidden">
          {/* Background Blobs */}
          <div className="absolute top-20 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-20 left-10 w-48 h-48 sm:w-80 sm:h-80 bg-gradient-to-tr from-pink-400 to-purple-400 rounded-full blur-3xl opacity-15"></div>
          
          {/* Dot Pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'radial-gradient(circle, #8B5CF6 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }}></div>

          <div className="relative max-w-6xl mx-auto text-center z-10">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-6 sm:mb-8 shadow-lg">
              <span className="text-lg sm:text-2xl">🎉</span>
              <span className="text-sm sm:text-base text-purple-700 font-semibold">{waitlistCount}+ creators waiting</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold mb-4 sm:mb-6 leading-tight px-2">
              <div className="text-gray-900">Stop Staring at Blank Captions.</div>
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Let AI Write Them</div>
              <div className="text-gray-900">in 3 Seconds.</div>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-xl text-gray-600 mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed px-4">
              Upload your photo. Get 5 perfect Instagram captions instantly.
            </p>
            <p className="text-base sm:text-xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
              Funny, deep, short, story, or question - pick your vibe.
            </p>

            {/* CTA Button */}
            <div className="flex flex-col items-center gap-3 sm:gap-4 px-4">
              <button onClick={scrollToWaitlist} className="px-6 sm:px-10 py-3 sm:py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-base sm:text-2xl font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2 sm:gap-3 whitespace-nowrap">
                Join Waitlist - Get 50% Off
                <span className="text-xl sm:text-3xl">✨</span>
              </button>
              <p className="text-xs sm:text-sm text-gray-500">No credit card • {waitlistCount}+ waiting</p>
            </div>

            {/* Hero Visual */}
            <div className="mt-12 sm:mt-20 relative">
              <div className="relative inline-block">
                {/* Floating Badges - Hidden on mobile */}
                <div className="hidden md:block absolute -left-20 top-20 px-4 py-2 bg-white rounded-full shadow-xl border border-purple-200 animate-float whitespace-nowrap">
                  <span className="text-lg">✨ Funny</span>
                </div>
                <div className="hidden md:block absolute -right-20 top-32 px-4 py-2 bg-white rounded-full shadow-xl border border-pink-200 animate-float-delayed whitespace-nowrap" style={{ animationDelay: '0.5s' }}>
                  <span className="text-lg">💭 Deep</span>
                </div>
                <div className="hidden md:block absolute -left-16 bottom-32 px-4 py-2 bg-white rounded-full shadow-xl border border-purple-200 animate-float whitespace-nowrap" style={{ animationDelay: '1s' }}>
                  <span className="text-lg">⚡ Short</span>
                </div>
                
                {/* Phone Mockup */}
                <div className="relative transform hover:rotate-0 transition-transform duration-500">
                  <div className="w-64 h-[480px] sm:w-80 sm:h-[600px] bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2.5rem] sm:rounded-[3rem] p-2 sm:p-3 shadow-2xl mx-auto">
                    <div className="w-full h-full bg-white rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden">
                      <div className="h-full flex flex-col">
                        {/* App Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-3 sm:p-4 text-white">
                          <h3 className="font-bold text-base sm:text-lg">Caption King</h3>
                        </div>
                        {/* Sample Image */}
                        <div className="h-36 sm:h-48 bg-gray-200">
                          <img src="https://public.readdy.ai/ai/img_res/c2aee5e9140cc9a0e97f5bd491561932.jpg" alt="Sample" className="w-full h-full object-cover" />
                        </div>
                        {/* Caption Cards */}
                        <div className="flex-1 p-2 sm:p-3 space-y-1.5 sm:space-y-2 overflow-hidden">
                          <div className="bg-purple-50 p-2 sm:p-3 rounded-lg sm:rounded-xl border border-purple-200">
                            <div className="text-xs font-semibold text-purple-600 mb-1">😂 Funny</div>
                            <div className="text-xs text-gray-700">Coffee: because adulting is hard ☕</div>
                          </div>
                          <div className="bg-pink-50 p-2 sm:p-3 rounded-lg sm:rounded-xl border border-pink-200">
                            <div className="text-xs font-semibold text-pink-600 mb-1">💭 Deep</div>
                            <div className="text-xs text-gray-700">Every cup tells a story...</div>
                          </div>
                          <div className="bg-purple-50 p-2 sm:p-3 rounded-lg sm:rounded-xl border border-purple-200">
                            <div className="text-xs font-semibold text-purple-600 mb-1">⚡ Short</div>
                            <div className="text-xs text-gray-700">Morning vibes ☕✨</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem/Solution Section */}
        <section className="py-12 sm:py-24 px-4 sm:px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              {/* Problem */}
              <div className="bg-gray-50 rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-gray-200 relative overflow-hidden">
                <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)',
                  backgroundSize: '10px 10px'
                }}></div>
                <div className="relative">
                  <div className="text-4xl sm:text-5xl mb-4 sm:mb-6">😫</div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">The Caption Struggle is Real</h2>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">❌</span>
                      <span className="text-gray-700 text-base sm:text-lg">Staring at blank screen for 20 minutes</span>
                    </li>
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">❌</span>
                      <span className="text-gray-700 text-base sm:text-lg">Copying competitors' captions (cringe)</span>
                    </li>
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">❌</span>
                      <span className="text-gray-700 text-base sm:text-lg">Posting without caption (RIP engagement)</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Solution */}
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                <div className="relative">
                  <div className="text-4xl sm:text-5xl mb-4 sm:mb-6">✨</div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Caption King Fixes It</h2>
                  <ul className="space-y-3 sm:space-y-4">
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">✅</span>
                      <span className="text-white text-base sm:text-lg">Upload photo → Get 5 captions in 3 seconds</span>
                    </li>
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">✅</span>
                      <span className="text-white text-base sm:text-lg">AI analyzes your image &amp; brand voice</span>
                    </li>
                    <li className="flex items-start gap-2 sm:gap-3">
                      <span className="text-lg sm:text-xl flex-shrink-0">✅</span>
                      <span className="text-white text-base sm:text-lg">Copy, edit, post. Done.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-white to-purple-50">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-4 sm:mb-6">
                <span className="text-lg sm:text-xl">✨</span>
                <span className="text-sm sm:text-base text-purple-700 font-semibold">Features</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">5 Caption Styles, Infinite Possibilities</h2>
              <p className="text-base sm:text-xl text-gray-600 px-4">Pick the vibe that matches your content</p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
              {/* Card 1 - Funny */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-purple-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">😂</div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Funny Captions</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">Make your followers laugh. Perfect for memes, behind-the-scenes, and relatable content.</p>
                <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-100">
                  <p className="text-xs sm:text-sm italic text-purple-700">"I'm not lazy, I'm on energy-saving mode 😴"</p>
                </div>
              </div>

              {/* Card 2 - Deep */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-purple-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">💭</div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Deep &amp; Thoughtful</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">Inspire your audience. Great for quotes, nature shots, and meaningful moments.</p>
                <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-100">
                  <p className="text-xs sm:text-sm italic text-purple-700">"Growth happens when you step outside your comfort zone 🌱"</p>
                </div>
              </div>

              {/* Card 3 - Short */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-purple-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">⚡</div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Short &amp; Punchy</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">Quick impact. Ideal for product shots, announcements, and fast scrollers.</p>
                <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-100">
                  <p className="text-xs sm:text-sm italic text-purple-700">"Vibes ✨"</p>
                </div>
              </div>

              {/* Card 4 - Question */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-purple-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">❓</div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Question Style</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">Boost engagement. Ask your followers, start conversations, build community.</p>
                <div className="bg-purple-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-purple-100">
                  <p className="text-xs sm:text-sm italic text-purple-700">"What's your go-to morning routine? 🌅"</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-purple-50 to-white">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 rounded-full mb-4 sm:mb-6">
                <span className="text-lg sm:text-xl">💰</span>
                <span className="text-sm sm:text-base text-purple-700 font-semibold">Pricing</span>
              </div>
              <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">Choose Your Plan</h2>
              <p className="text-base sm:text-xl text-gray-600 px-4">Start free, upgrade when you need more</p>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-8 sm:mb-12">
              {/* FREE */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-gray-200 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">FREE</h3>
                <div className="mb-6 sm:mb-8">
                  <div className="text-4xl sm:text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">$0</div>
                  <div className="text-gray-500 text-xs sm:text-sm">forever</div>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">3 free captions</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">All 5 caption styles</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">Copy to clipboard</span>
                  </li>
                </ul>
                <button onClick={scrollToWaitlist} className="w-full py-3 sm:py-4 border-2 border-purple-600 text-purple-600 text-sm sm:text-base rounded-full font-bold hover:bg-purple-50 transition-all whitespace-nowrap">
                  Try Free
                </button>
              </div>

              {/* PRO - Highlighted */}
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-2xl relative md:scale-105 hover:scale-110 transition-all duration-300">
                {/* Popular Badge */}
                <div className="absolute -top-3 sm:-top-4 right-6 sm:right-8 px-3 sm:px-4 py-1.5 sm:py-2 bg-yellow-400 text-gray-900 rounded-full font-bold text-xs sm:text-sm shadow-lg whitespace-nowrap">
                  ⭐ POPULAR
                </div>
                
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">PRO</h3>
                <div className="mb-6 sm:mb-8">
                  <div className="text-4xl sm:text-6xl font-bold text-white mb-2">$4.99</div>
                  <div className="text-purple-100 text-xs sm:text-sm">per month</div>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-white text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-white text-sm sm:text-base">Unlimited captions</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-white text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-white text-sm sm:text-base">Save favorites</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-white text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-white text-sm sm:text-base">Priority support</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-white text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-white text-sm sm:text-base">Early access to features</span>
                  </li>
                </ul>
                <button onClick={scrollToWaitlist} className="w-full py-3 sm:py-4 bg-white text-gray-900 text-sm sm:text-base rounded-full font-bold hover:shadow-xl transition-all whitespace-nowrap mb-3 sm:mb-4">
                  Join Waitlist
                </button>
                <p className="text-white text-xs sm:text-sm text-center leading-relaxed">
                  Launching soon. Get 50% OFF → <span className="font-bold">$2.49/mo</span>
                </p>
              </div>

              {/* CREDITS */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-gray-200 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8">CREDITS</h3>
                <div className="mb-6 sm:mb-8">
                  <div className="text-4xl sm:text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">$2.99</div>
                  <div className="text-gray-500 text-xs sm:text-sm">one-time</div>
                </div>
                <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">10 captions ($0.30 each)</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">Never expire</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <span className="text-green-500 text-lg sm:text-xl flex-shrink-0">✓</span>
                    <span className="text-gray-700 text-sm sm:text-base">All caption styles</span>
                  </li>
                </ul>
                <button onClick={scrollToWaitlist} className="w-full py-3 sm:py-4 border-2 border-purple-600 text-purple-600 text-sm sm:text-base rounded-full font-bold hover:bg-purple-50 transition-all whitespace-nowrap">
                  Join Waitlist
                </button>
              </div>
            </div>

            {/* Bottom Messages */}
            <div className="text-center space-y-3 sm:space-y-4 px-4">
              <p className="text-base sm:text-xl text-gray-700">
                🎉 Early waitlist members get lifetime 50% discount
              </p>
              <p className="text-sm sm:text-lg text-gray-600">
                💯 Launching February 2026. Join waitlist to lock in 50% OFF forever.
              </p>
            </div>
          </div>
        </section>

        {/* Waitlist Section */}
        <section id="waitlist" className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-800">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-5xl sm:text-6xl mb-4 sm:mb-6">🚀</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">Join {waitlistCount}+ Creators</h2>
            <p className="text-base sm:text-xl text-purple-100 mb-8 sm:mb-12 px-4">Get 50% off when we launch. Plus early access to all features.</p>

            {/* Form */}
            <form onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 space-y-5 sm:space-y-6">
              {/* First Name */}
              <div className="text-left">
                <label htmlFor="firstName" className="block text-xs sm:text-sm text-gray-600 mb-2">First Name (optional)</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 rounded-lg sm:rounded-xl border-0 focus:ring-2 focus:ring-purple-600 outline-none text-sm sm:text-base"
                />
              </div>

              {/* Email */}
              <div className="text-left">
                <label htmlFor="email" className="block text-xs sm:text-sm text-gray-600 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 rounded-lg sm:rounded-xl border-0 focus:ring-2 focus:ring-purple-600 outline-none text-sm sm:text-base"
                />
              </div>

              {/* Source Dropdown */}
              <div className="text-left">
                <label htmlFor="source" className="block text-xs sm:text-sm text-gray-600 mb-2">How did you hear about us?</label>
                <select
                  id="source"
                  name="source"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-gray-50 rounded-lg sm:rounded-xl border-0 focus:ring-2 focus:ring-purple-600 outline-none text-sm sm:text-base cursor-pointer"
                  required
                >
                  <option value="">Select an option</option>
                  <option value="Twitter">Twitter</option>
                  <option value="Reddit">Reddit</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Friend">Friend</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 sm:py-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-base sm:text-xl font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Save My Spot - Get 50% Off 🎉'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="text-green-600 font-semibold text-center text-sm sm:text-base">
                  🎉 You're on the list! Check your email for confirmation.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="text-red-600 font-semibold text-center text-sm sm:text-base">
                  ❌ Something went wrong. Please try again.
                </div>
              )}

              {/* Small Text */}
              <p className="text-xs sm:text-sm text-gray-500 text-center">
                Join {waitlistCount}+ creators. No spam, unsubscribe anytime.
              </p>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 py-6 sm:py-8 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            {/* Left */}
            <div className="text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-6 h-6 sm:w-8 sm:h-8 object-contain" />
                <span className="text-base sm:text-xl font-bold text-white">Caption King</span>
              </div>
              <a href="mailto:hello@tryaikit.com" className="text-gray-400 text-xs sm:text-sm hover:text-white hover:underline transition-colors">
                hello@tryaikit.com
              </a>
            </div>

            {/* Center */}
            <div className="text-gray-400 text-xs sm:text-sm text-center">
              © 2025 TryAIKit. All rights reserved.
            </div>

            {/* Right */}
            <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm">
              <a href="/privacy" className="text-gray-400 hover:text-white hover:underline transition-colors whitespace-nowrap">
                Privacy Policy
              </a>
              <span className="text-gray-600">|</span>
              <a href="/terms" className="text-gray-400 hover:text-white hover:underline transition-colors whitespace-nowrap">
                Terms of Service
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
